import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

const TrackContext = createContext(undefined);

/**
 * TrackProvider - Provides track data and related helpers to child components
 *
 * Manages the current track state including:
 * - Track metadata (id, title, slug, description)
 * - Children content (weeks, action hours, etc.)
 * - Filtered access to children by type
 */
export const TrackProvider = ({ children, initialTrackList = [], initialTrack = null }) => {
  const [track, setTrack] = useState(initialTrack);
  const [trackList, setTrackList] = useState(initialTrackList);

  // Sync state when initialTrack prop changes (e.g., after async fetch completes)
  useEffect(() => {
    setTrack(initialTrack);
  }, [initialTrack]);

  useEffect(() => {
    setTrackList(initialTrackList);
  }, [initialTrackList]);

  // Memoized derived data to prevent unnecessary recalculations
  const weeks = useMemo(() => {
    if (!track?.Children || !track?.childrenIndexes?.week) {
      return [];
    }
    return track.childrenIndexes.week.map(index => track.Children[index]);
  }, [track]);

  const actionHours = useMemo(() => {
    if (!track?.Children || !track?.childrenIndexes?.videoResource) {
      return [];
    }
    return track.childrenIndexes.videoResource.map(index => track.Children[index]);
  }, [track]);

  const allContent = useMemo(() => {
    return track?.Children || [];
  }, [track]);

  // Get content by type
  const getContentByType = useCallback(
    type => {
      if (!track?.Children || !track?.childrenIndexes?.[type]) {
        return [];
      }
      return track.childrenIndexes[type].map(index => track.Children[index]);
    },
    [track]
  );

  // Find specific content by slug
  const findContentBySlug = useCallback(
    slug => {
      if (!track?.Children) return null;
      return track.Children.find(child => child.slug === slug);
    },
    [track]
  );

  // Find specific content by id
  const findContentById = useCallback(
    id => {
      if (!track?.Children) return null;
      return track.Children.find(child => child.id === id);
    },
    [track]
  );

  // Get track metadata
  const metadata = useMemo(() => {
    if (!track) return null;
    return {
      id: track.id,
      name: track.name,
      title: track.title,
      slug: track.slug,
      description: track.displayProps?.description || track.data?.description,
      image: track.displayProps?.image,
      type: track.type,
      published: track.published,
      free: track.free,
      createdAt: track.createdAt
    };
  }, [track]);

  // Get content count by type
  const getContentCount = useCallback(
    type => {
      if (!track?.childrenIndexes?.[type]) return 0;
      return track.childrenIndexes[type].length;
    },
    [track]
  );

  // Check if track has content of a specific type
  const hasContentType = useCallback(
    type => {
      return track?.childrenIndexes?.[type]?.length > 0;
    },
    [track]
  );

  // Get 1-based week number by slug
  const getWeekNumber = useCallback(
    slug => {
      if (!slug || !weeks.length) return 0;
      const index = weeks.findIndex(w => w.slug === slug);
      return index === -1 ? 0 : index + 1;
    },
    [weeks]
  );

  const value = {
    // Core track data
    track,
    trackList,
    setTrack,
    setTrackList,

    // Metadata
    metadata,

    // Content access
    weeks,
    actionHours,
    allContent,

    // Helper functions
    getContentByType,
    findContentBySlug,
    findContentById,
    getContentCount,
    hasContentType,
    getWeekNumber,

    // Convenience flags
    hasTrack: !!track,
    isLoading: track === undefined
  };

  return <TrackContext.Provider value={value}>{children}</TrackContext.Provider>;
};

TrackProvider.propTypes = {
  children: PropTypes.node.isRequired,
  trackList: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.number,
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string
      }),
      description: PropTypes.string,
      slug: PropTypes.string,
      hidden: PropTypes.bool
    })
  ),
  initialTrack: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    title: PropTypes.string,
    slug: PropTypes.string,
    type: PropTypes.string,
    published: PropTypes.bool,
    free: PropTypes.bool,
    createdAt: PropTypes.string,
    displayProps: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.string,
      image: PropTypes.shape({
        url: PropTypes.string,
        alt: PropTypes.string
      })
    }),
    data: PropTypes.shape({
      description: PropTypes.string
    }),
    Children: PropTypes.arrayOf(PropTypes.shape({})),
    childrenIndexes: PropTypes.shape({})
  })
};

/**
 * useTrack - Hook to access track context
 *
 * @returns {Object} Track context value
 * @throws {Error} If used outside TrackProvider
 */
export const useTrack = () => {
  const context = useContext(TrackContext);

  if (context === undefined) {
    throw new Error('useTrack must be used within a TrackProvider');
  }

  return context;
};

/**
 * withTrack - HOC to inject track context as props
 *
 * @param {React.Component} Component - Component to wrap
 * @returns {React.Component} Wrapped component with track prop
 */
export const withTrack = Component => {
  const WrappedComponent = props => {
    const trackContext = useTrack();
    return <Component {...props} track={trackContext} />;
  };

  WrappedComponent.displayName = `withTrack(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

export default TrackContext;
